import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ApiService,
    GraphQLResponse,
    Mk2Request,
    Mk2Response
} from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';
import { sumMonies } from '../../helpers/money-operations';
import { mapPayableStatusLegacyToInvoiceFundingStatus } from '../../helpers/status-mapping';
import {
    BuyerAccountInvoiceGroupDetail,
    BuyerAccountInvoiceGroupItem,
    PayablesInput,
    PayablesResponse,
    Query
} from '../../model/models';

export class GetBuyerAccountInvoiceGroupRequest extends Mk2Request {
    buyerKeys?: string[];
    groupKey?: string;
    pendingCreditMemosOnly?: boolean;

    constructor(initial?: Partial<GetBuyerAccountInvoiceGroupRequest>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

export class GetBuyerAccountInvoiceGroupResponse extends Mk2Response {
    invoiceGroup?: BuyerAccountInvoiceGroupDetail;
    message?: string;
    ok?: boolean;

    constructor(initial?: Partial<GetBuyerAccountInvoiceGroupResponse>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

@Injectable({ providedIn: 'root' })
export class GetBuyerAccountInvoiceGroupService extends ApiService<
    GetBuyerAccountInvoiceGroupRequest,
    GetBuyerAccountInvoiceGroupResponse
> {
    public requestType = GetBuyerAccountInvoiceGroupRequest;
    public responseType = GetBuyerAccountInvoiceGroupResponse;
    public endpointName = 'BuyerAccount/InvoiceGroup/GetBuyerAccountInvoiceGroup';
    public serviceName = 'financing';
    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'BuyerAccount/InvoiceGroup/GetBuyerAccountInvoiceGroup';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: GetBuyerAccountInvoiceGroupRequest
    ): { query: string } | GetBuyerAccountInvoiceGroupRequest {
        if (this._isPayLaterEnabled) {
            const input: PayablesInput = {
                payableIds: [+req.groupKey],
                pendingCreditMemosOnly: req.pendingCreditMemosOnly,
                companyFilter: { buyerIds: req.buyerKeys ?? [] }
            };
            const amount = { cents: true, currencyCode: true };
            const query = jsonToGraphQLQuery({
                payables: {
                    __args: { input },
                    rows: {
                        statusLegacy: true,
                        transfers: {
                            transferDate: true
                        },
                        relationship: { supplier: { company: { name: true } } },
                        approvals: {
                            amount,
                            id: true,
                            canBeRevoked: true,
                            adjustedDueDate: true,
                            statusLegacy: true,
                            invoice: {
                                date: true,
                                invoiceNumber: true
                            }
                        },
                        creditMemos: {
                            amount,
                            id: true,
                            date: true,
                            canBeRevoked: true,
                            creditMemoNumber: true
                        }
                    }
                }
            } as Query);

            return { query: `{${query}}` };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<PayablesResponse> | GetBuyerAccountInvoiceGroupResponse
    ): GetBuyerAccountInvoiceGroupResponse {
        if (this._isPayLaterEnabled) {
            const { rows } = (res as GraphQLResponse<PayablesResponse>).data.payables;
            const payable = rows[0];

            const { supplier } = payable.relationship;
            const credits = sumMonies(payable.creditMemos.map((cm) => cm.amount) ?? []);
            const totalInvoiceAmount = sumMonies(payable.approvals.map((a) => a.amount) ?? []);

            const total = sumMonies([credits, totalInvoiceAmount]);

            return new GetBuyerAccountInvoiceGroupResponse({
                ok: true,
                pagination: null,
                invoiceGroup: <BuyerAccountInvoiceGroupDetail>{
                    credits,
                    totalInvoiceAmount,
                    invoiceStatus: mapPayableStatusLegacyToInvoiceFundingStatus(
                        payable.statusLegacy
                    ),
                    paymentDate: payable.transfers?.[0]?.transferDate,
                    sellerName: supplier.company.name,
                    total,
                    items: [
                        ...payable.creditMemos.map(
                            (creditMemo) =>
                                <BuyerAccountInvoiceGroupItem>{
                                    canRevoke: creditMemo.canBeRevoked,
                                    itemKey: creditMemo.id.toString(),
                                    amount: creditMemo.amount,
                                    invoiceDate: creditMemo.date,
                                    invoiceNumber: creditMemo.creditMemoNumber
                                }
                        ),
                        ...payable.approvals.map(
                            (approval) =>
                                <BuyerAccountInvoiceGroupItem>{
                                    canRevoke: approval.canBeRevoked,
                                    itemKey: approval.id.toString(),
                                    amount: approval.amount,
                                    dueDate: approval.adjustedDueDate,
                                    invoiceDate: approval.invoice.date,
                                    invoiceNumber: approval.invoice.invoiceNumber,
                                    status: approval.statusLegacy
                                }
                        )
                    ]
                }
            });
        }

        return res as GetBuyerAccountInvoiceGroupResponse;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
